<template>

    <!-- If user clicks outside the dialog @input is triggered -->
    <v-dialog v-model="dialogOpen"
              width="80%"
              class="pa-0"
              persistent
              @input="emit()"
              transition="fade-transition">

        <toolbar-page :title="multiple ? 'Data variables selection' :'Data variable selection'" icon="mdi-layers">
            <template slot="buttons">

                <!-- SEARCH BOX -->
                <v-text-field solo
                              clearable
                              background-color="transparent"
                              prepend-icon="mdi-magnify"
                              hide-details
                              placeholder="Search"
                              @keyup="performSearch"
                              @click:clear="searchCleared"
                              v-model="searchTerm"
                              class="mr-15 elevation-0"
                ></v-text-field>

                <v-btn v-if="multiple" color="primary" @click="emit">DONE</v-btn>
                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn @click="dialogOpen=false" v-on="on" icon>
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close without selection</span>
                </v-tooltip>
            </template>
        </toolbar-page>

        <!-- GROUP SELECTOR -->
        <v-card class="parent" ref="parent">

            <v-card flat class="groups">

                <v-card-text class="pa-0">

                    <v-subheader>
                        Groups
                        <span class="caption grey--text text--lighten-2 pl-1">({{ dataVariableGroupCount }})</span>
                    </v-subheader>

                    <div class="groupsVerticalScrollPanel" ref="panel">

                        <v-list v-if="selectedGroup">

                            <v-list-item
                                one-line
                                dark
                                v-for="item in variableGroups"
                                :key="item.id"
                                ripple
                                :class="selectedGroup.id===item.id ? 'primary group': 'secondary group'"
                                @click="groupClicked(item)">

                                <v-list-item-avatar>
                                    <v-badge v-if="item.selectedCount > 0"
                                             color="grey"
                                             right
                                    >
                                        <template v-slot:badge>
                                            <span>{{ item.selectedCount }}</span>
                                        </template>
                                        <v-icon color="grey lighten-3">
                                            mdi-gamepad-circle
                                        </v-icon>
                                    </v-badge>

                                    <v-icon v-else color="grey lighten-1">
                                        mdi-gamepad-circle
                                    </v-icon>

                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ item.name }}
                                        <span class="caption grey--text text--lighten-1">({{ item.count }})</span>
                                    </v-list-item-title>
                                </v-list-item-content>
                                <v-list-item-avatar>
                                    <v-icon>
                                        {{ selectedGroup.id === item.id ? 'mdi-chevron-right' : '' }}
                                    </v-icon>
                                </v-list-item-avatar>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-card-text>
            </v-card>

            <!-- DATA VARIABLES -->
            <v-card flat class="ml-1 dataVariables">
                <v-card-text class="pt-0">

                    <v-subheader>
                        Variables
                    </v-subheader>

                    <div class="d-flex flex-wrap align-start flex-column variablesScrollHorizontally"
                         :style="variableContainerStyle">

                        <v-scale-transition>
                            <v-alert v-show="!selectedGroup"
                                     prominent
                                     style="width: 100%"
                                     icon="mdi-alert"
                                     type="warning">
                                No results found
                            </v-alert>
                        </v-scale-transition>

                        <div v-if="selectedGroup">
                            <div v-for="variable in selectedGroup.variables">

                                <v-chip :key="variable.id"
                                        pill
                                        class="ma-1"
                                        :input-value="inSelected(variable)"
                                        :color="inSelected(variable) ? 'primary': 'grey darken-1'"
                                        @click="chipClicked(variable)">
                                    <v-avatar left class="grey darken-2">
                                        <v-icon>{{ getIcon(variable.type) }}</v-icon>
                                    </v-avatar>
                                    {{ variable.name }}
                                </v-chip>
                            </div>
                        </div>
                    </div>

                </v-card-text>
            </v-card>
        </v-card>

        <v-container fluid v-if="debug">
            <v-row>
                <v-col cols="6">
                    <v-card>
                        <v-card-text>
                            <json :data="selectedGroup"></json>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

    </v-dialog>

</template>

<script>

import Json from 'vue-json-pretty';
import ToolbarPage from './ToolbarPage';

export default {

    components: {
        Json,
        ToolbarPage
    },

    props: {
        // for v-model binding
        value: {
            type: Boolean,
            default: false,
            required: true
        },
        dataVariables: {
            required: true,
            type: Array
        },
        selected: {
            required: false,
            type: Array,
            default: () => []
        },
        multiple: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    watch: {
        dataVariables(variables) {
            this.setup();
        },

        selected(selected) {
            this.selectedVariables = [...selected];
            this.selectGroupAutomatically();
        },
    },

    mounted() {
        this.setup();
    },

    data() {
        return {
            debug: false,
            initialSelectionComplete: false,
            variableGroups: [],
            selectedGroup: null,
            selectedVariables: [],
            windowHeight: null,
            window: {
                width: null,
                height: null,
            },
            variableContainerStyle: '',
            searchTerm: null,
        }
    },

    computed: {

        dialogOpen: {
            get() {
                return this.value;
            },
            set() {
                this.closeDialog();
            }
        },

        dataVariableGroupCount() {
            return this.variableGroups.length;
        },
    },

    methods: {

        searchCleared() {
            this.searchTerm = null;
            this.setup();
        },

        performSearch() {
            this.setup();
        },

        setup() {
            this.selectedVariables = [...this.selected];
            this.processVariables();
            this.selectGroupAutomatically();

            window.addEventListener('resize', () => {
                this.handleResize();
            });

            this.handleResize();
            this.matchHeight();
        },

        matchHeight() {
            let height = this.window.height * 0.7;
            if (height < 300) height = 300;
            this.variableContainerStyle = 'height: ' + (height) + 'px;';
        },

        handleResize() {
            this.window.width = window.innerWidth;
            this.window.height = window.innerHeight;
            this.matchHeight();
        },

        getIcon(type) {
            return this.$store.state.dataVariableIcons[type];
        },

        selectGroupAutomatically() {

            if (this.selected.length > 0) {

                // If variable doesn't have group
                if (this.selected[0].group === null) {
                    this.selectedGroup = this.variableGroups[0];
                    return;
                }

                // Find group
                let found = this.variableGroups.find(variableGroup => {
                    return variableGroup.name === this.selected[0].group.name;
                });

                if (found) {
                    this.selectedGroup = found;
                    return;
                }
            }

            // When searching do not check local storage
            if (!this.searchTerm && localStorage.variablePickerSelectedGroup) {
                this.selectedGroup = JSON.parse(localStorage.variablePickerSelectedGroup);
            } else {
                // Just select the first group
                if (this.searchTerm && this.variableGroups.length === 0) {
                    this.selectedGroup = null;
                } else {
                    this.selectedGroup = this.variableGroups[0];
                }
            }
        },

        processVariables() {
            let variableGroups = [];

            // Scan for some counts
            this.dataVariables.forEach((variable) => {

                let include = true;

                if (this.searchTerm && !variable.name.includes(this.searchTerm.toUpperCase())) include = false;

                if (include) {
                    let groupId = !variable.group ? null : variable.group.id;
                    let groupName = !variable.group ? 'No group' : variable.group.name;
                    let magic = !variable.group;

                    let found = variableGroups.find((group) => group.id === groupId);

                    if (!found) {
                        variableGroups.push({
                            id: groupId,
                            name: groupName,
                            count: 1,
                            magic: magic,
                            variables: [variable]
                        });

                    } else {
                        found.variables.push(variable);
                        found.count++;
                    }
                }
            });

            // Alpha sort variable groups
            this.variableGroups = variableGroups.sort((a, b) => {
                if (a.magic) return -1;
                return a.name < b.name ? -1 : 1;
            });
        },

        // TODO: This is very slow here, get's really bad with ton of variables!!!
        inSelected(variable) {

            let index = this.selectedVariables.find((selected) => {
                return selected.id === variable.id;
            });
            return !!index;
        },

        chipClicked(variable) {

            // See if item is already selected
            let index = this.selectedVariables.findIndex(selected => selected.id === variable.id);

            if (index === -1) {
                if (!this.multiple) {
                    this.selectedVariables = [];
                }
                this.selectedVariables.push(variable);
            } else {
                this.selectedVariables.splice(index, 1);
            }

            // Single close dialog after data variable has been clicked
            if (!this.multiple) {
                this.emit();
            }

            let found = false;

            if (variable.group) {
                found = this.variableGroups.find(group => group.id === variable.group.name);
            }

            if (found) {
                found.count++;
            }
        },

        groupClicked(group) {
            this.selectedGroup = group;

            localStorage.variablePickerSelectedGroup = JSON.stringify(group);
        },

        closeDialog() {
            this.$emit('input', false);
        },

        emit() {
            if (this.selectedVariables.length !== 0) {
                this.$emit('change', this.selectedVariables);
                this.closeDialog();
            }
        }
    }
}
</script>

<style scoped>

.groupsVerticalScrollPanel {
    overflow-y: auto;
    height: 100%;
}

.variablesScrollHorizontally {
    overflow-x: auto;
    width: 100%;
}

.parent {
    display: flex;
    align-items: flex-start;
    width: 100%;
}

.groups {
    height: 100%;
}

.group {
    height: 40px;
}

.dataVariables {
    width: 100%;
}

</style>
